import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/notifications — single home for system events: answered table
 * questions, accepted invites, new safety signal replies, invite requests
 * incoming. Built from real events (see dataService.listNotifications);
 * read/dismissed state is kept on this device.
 *
 * Filter tabs: All / Unread / Table
 * Each row links to the deep destination (table question, invite, etc).
 */

const EASE = [0.22, 1, 0.36, 1];


function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(/Z|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (!Number.isFinite(mins)) return '';
  if (mins < 60) return mins < 1 ? 'just now' : `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return days < 30 ? `${days} day${days === 1 ? '' : 's'} ago` : then.toLocaleDateString();
}

const TONE = {
  blue:   { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
  green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
  amber:  { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  red:    { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
  purple: { bg: '#F1E8FE',                fg: '#5B21B6',               solid: '#7C3AED' },
  gray:   { bg: 'var(--cwm-gray-100)',    fg: 'var(--cwm-gray-700)',   solid: 'var(--cwm-gray-500)' },
};

const FILTERS = [
  { id: 'all',     label: 'All',     match: () => true },
  { id: 'unread',  label: 'Unread',  match: (n) => n.unread },
  { id: 'table',   label: 'Table',   match: (n) => /invite|table_answer/.test(n.type) },
];

export default function UnifiedNotifications() {
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      try {
        const live = await dataService.listNotifications();
        if (!cancelled) setItems(Array.isArray(live) ? live : []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate]);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    return items.filter(f.match);
  }, [items, filter]);

  const unreadCount = useMemo(() => items.filter((n) => n.unread).length, [items]);

  const markRead = (n) => {
    setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, unread: false } : x));
    if (n.db_id) dataService.markNotificationRead(n.db_id).catch(() => {});
  };
  const markAllRead = () => {
    setItems((prev) => prev.map((x) => ({ ...x, unread: false })));
    dataService.markAllNotificationsRead(items.map((x) => x.id)).catch(() => {});
  };
  const dismiss = (n) => {
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    if (n.db_id) dataService.dismissNotification(n.db_id).catch(() => {});
  };

  const onRowClick = (n) => {
    markRead(n);
    const target = n.deepLink || n.deep_link;
    if (target) navigate(target);
  };

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{ textAlign: 'center', marginBottom: 22 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <BellIcon size={12} /> NOTIFICATIONS
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            {unreadCount > 0 ? <>You have <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{unreadCount}</em> {unreadCount === 1 ? 'new thing' : 'new things'}.</> : 'All caught up.'}
          </h1>
          <p style={{ maxWidth: 460, margin: '0 auto', fontSize: 14, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            Table invites and answers to your questions — all in one place.
          </p>
        </motion.section>

        <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--cwm-gray-50)', border: '1px solid var(--cwm-gray-200)', borderRadius: 999, marginBottom: 14, overflowX: 'auto' }}>
          {FILTERS.map((f) => {
            const on = filter === f.id;
            const count = items.filter(f.match).length;
            return (
              <button key={f.id} type="button" onClick={() => setFilter(f.id)}
                      style={{
                        flex: 1, minWidth: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        padding: '9px 12px', borderRadius: 999,
                        background: on ? '#fff' : 'transparent',
                        color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-700)',
                        border: 'none', cursor: 'pointer',
                        fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 900, whiteSpace: 'nowrap',
                        boxShadow: on ? '0 4px 12px rgba(10,37,64,0.08)' : 'none',
                        transition: 'all 180ms ease',
                      }}>
                {f.label}
                <span style={{
                  padding: '1px 7px', borderRadius: 999,
                  background: on ? 'var(--cwm-blue-pale)' : 'var(--cwm-gray-200)',
                  color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-500)',
                  fontSize: 10.5, fontWeight: 900,
                }}>{count}</span>
              </button>
            );
          })}
        </div>

        {unreadCount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button type="button" onClick={markAllRead}
                    style={{ background: 'transparent', border: 'none', color: 'var(--cwm-blue-deep)', cursor: 'pointer', fontSize: 12.5, fontWeight: 800, textDecoration: 'underline' }}>
              Mark all as read
            </button>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="cwm-sheet" style={{ padding: 48, textAlign: 'center' }}>
            <div style={{ width: 60, height: 60, margin: '0 auto 14px', borderRadius: '50%', background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)', display: 'grid', placeItems: 'center', border: '1.5px dashed var(--cwm-blue-light)' }}>
              <BellIcon size={24} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 6 }}>
              Nothing here
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cwm-gray-700)', maxWidth: 380, margin: '0 auto' }}>
              You're all caught up. New activity shows up here — table invites and answers to your questions.
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            <AnimatePresence initial={false}>
              {filtered.map((n, i) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  index={i}
                  onClick={() => onRowClick(n)}
                  onDismiss={() => dismiss(n)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function NotificationRow({ notification, index, onClick, onDismiss }) {
  const t = TONE[notification.tone] || TONE.blue;
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 60 }}
      transition={{ duration: 0.28, delay: index * 0.04, ease: EASE }}
      whileHover={{ x: 2 }}
      onClick={onClick}
      className="cwm-sheet"
      style={{
        padding: 16,
        cursor: 'pointer',
        display: 'flex', gap: 14, alignItems: 'flex-start',
        borderLeft: notification.unread ? `4px solid ${t.solid}` : '4px solid transparent',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 12,
        background: t.bg, color: t.fg,
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <NotifIcon name={notification.icon} size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
            {notification.title}
            {notification.unread && (
              <span style={{
                display: 'inline-block', verticalAlign: 'middle', marginLeft: 8,
                width: 8, height: 8, borderRadius: 999, background: t.solid,
              }}/>
            )}
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)', whiteSpace: 'nowrap' }}>
            {timeAgo(notification.at)}
          </span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--cwm-gray-700)', lineHeight: 1.5 }}>
          {notification.body}
        </div>
      </div>
      <button type="button" onClick={(e) => { e.stopPropagation(); onDismiss(); }} aria-label="dismiss"
              style={{
                width: 28, height: 28, borderRadius: 999,
                background: 'transparent', color: 'var(--cwm-gray-500)',
                border: 'none', cursor: 'pointer',
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
      </button>
    </motion.article>
  );
}

function NotifIcon({ name, size = 18 }) {
  const paths = {
    chat:   'M21 12a9 9 0 01-4.5 7.8L17 22l-4-2a9 9 0 119-8z',
    check:  'M5 13l4 4L19 7',
    shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
    mail:   'M3 5h18v14H3zM3 7l9 6 9-6',
    clock:  'M12 3a9 9 0 100 18 9 9 0 000-18zM12 7v5l3 2',
    card:   'M3 7h18v10H3zM3 11h18M7 15h2',
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d={paths[name] || paths.chat}/>
    </svg>
  );
}

function BellIcon({ size = 14 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0"/></svg>;
}
